import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { caissesAPI, type Caisse } from '../../api/caisses'
import { BankInfoModal } from '../../components/livreur/BankInfoModal'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number) {
  return n.toLocaleString('fr-TN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatRow({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.12)',
    }}>
      <span style={{ fontSize: '.87rem', opacity: 0.82 }}>{label}</span>
      <b style={{ fontSize: accent ? '1.05rem' : '.95rem', letterSpacing: accent ? '.01em' : 0 }}>
        {value}
      </b>
    </div>
  )
}

function ProgressBar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0
  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.8rem', opacity: .75, marginBottom: 4 }}>
        <span>{value} livrés / {max} assignés</span>
        <span>{pct}%</span>
      </div>
      <div style={{ height: 6, borderRadius: 4, background: 'rgba(255,255,255,0.2)' }}>
        <div style={{
          height: '100%', borderRadius: 4,
          background: pct >= 80 ? '#4ade80' : pct >= 50 ? '#fbbf24' : '#f87171',
          width: `${pct}%`, transition: 'width .4s',
        }} />
      </div>
    </div>
  )
}

function StatutBadge({ statut }: { statut: Caisse['statut'] }) {
  const config = {
    ouverte:  { bg: 'rgba(74,222,128,0.18)', color: '#4ade80', label: 'Ouverte' },
    cloturee: { bg: 'rgba(248,113,113,0.18)', color: '#f87171', label: 'Clôturée' },
    payee:    { bg: 'rgba(96,165,250,0.18)', color: '#60a5fa', label: 'Payée' },
  }[statut]

  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 20,
      fontSize: '.78rem', fontWeight: 600,
      background: config.bg, color: config.color,
    }}>
      {statut === 'ouverte' ? '● ' : statut === 'cloturee' ? '◆ ' : '✓ '}{config.label}
    </span>
  )
}

function Skeleton({ width = '100%', height = 14 }: { width?: string | number; height?: number }) {
  return (
    <div style={{
      width, height, borderRadius: 6,
      background: 'linear-gradient(90deg,rgba(255,255,255,.08) 25%,rgba(255,255,255,.18) 50%,rgba(255,255,255,.08) 75%)',
      backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite',
    }} />
  )
}

// ─── Caisse card ──────────────────────────────────────────────────────────────

function CaisseCard({
  caisse,
  onEditBank,
}: {
  caisse: Caisse
  onEditBank: () => void
}) {
  const isWeek = caisse.type === 'week'
  const bg = isWeek
    ? 'linear-gradient(135deg, #1e3a5f 0%, #2d6a9f 100%)'
    : 'linear-gradient(135deg, #14532d 0%, #1d9e75 100%)'

  return (
    <div style={{
      background: bg, borderRadius: 18, padding: '22px 20px',
      color: '#fff', boxShadow: '0 4px 24px rgba(0,0,0,.18)',
      marginBottom: 20,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: '.78rem', opacity: .7, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '.06em' }}>
            {isWeek ? 'Semaine' : 'Journée'}
          </div>
          <div style={{ fontWeight: 700, fontSize: '1rem' }}>
            {isWeek ? 'Virement bancaire' : 'Cash à remettre'}
          </div>
          <div style={{ fontSize: '.8rem', opacity: .65, marginTop: 2 }}>
            {fmtDate(caisse.dateDebut)}
            {isWeek && ` — ${fmtDate(caisse.dateFin)}`}
          </div>
        </div>
        <StatutBadge statut={caisse.statut} />
      </div>

      {/* Metrics */}
      <StatRow label="Brut COD perçu"       value={`${fmt(caisse.montantBrut)} TND`} />
      <StatRow label="Mon gain (livreur)"    value={`${fmt(caisse.gainLivreur)} TND`} />
      <StatRow label="Commission TariqGo"   value={`${fmt(caisse.commissionTariqGo)} TND`} />
      <div style={{ borderBottom: '1px solid rgba(255,255,255,0.12)', padding: '10px 0' }}>
        <ProgressBar value={caisse.totalColisLivres} max={caisse.totalColisAssignes} />
      </div>
      <StatRow
        label={isWeek ? 'Net TariqGo (semaine)' : 'À remettre (net)'}
        value={`${fmt(caisse.montantNet)} TND`}
        accent
      />

      {/* Virement info for week */}
      {isWeek && (
        <div style={{
          marginTop: 16, padding: '12px 14px', borderRadius: 10,
          background: 'rgba(255,255,255,0.1)', fontSize: '.83rem',
        }}>
          {caisse.virementInfo?.rib ? (
            <>
              <div style={{ opacity: .75, marginBottom: 4 }}>Informations bancaires</div>
              <div><b>RIB :</b> {caisse.virementInfo.rib}</div>
              <div><b>Banque :</b> {caisse.virementInfo.banque} · {caisse.virementInfo.nomTitulaire}</div>
              {caisse.virementInfo.dateVirement && (
                <div style={{ marginTop: 4, opacity: .7 }}>
                  Viré le {fmtDate(caisse.virementInfo.dateVirement)}
                  {caisse.virementInfo.reference && ` — Réf. ${caisse.virementInfo.reference}`}
                </div>
              )}
            </>
          ) : (
            <div style={{ opacity: .75 }}>⚠ Aucune info bancaire enregistrée</div>
          )}
          {caisse.statut !== 'payee' && (
            <button
              onClick={onEditBank}
              style={{
                marginTop: 10, width: '100%', padding: '8px 0', borderRadius: 8, border: 'none',
                background: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 600,
                fontSize: '.82rem', cursor: 'pointer',
              }}
            >
              ✏ Modifier mes infos bancaires
            </button>
          )}
          {caisse.statut === 'ouverte' && (
            <div style={{ marginTop: 8, opacity: .65, fontSize: '.8rem', textAlign: 'center' }}>
              ⏳ Virement prévu le vendredi
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Historique section ───────────────────────────────────────────────────────

function HistoriqueSection({ caisses, mode }: { caisses: Caisse[]; mode: 'day' | 'week' }) {
  return (
    <div>
      <h3 style={{ fontSize: '.88rem', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>
        Historique
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {caisses.map(c => (
          <div key={c._id} className="card" style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '.9rem', color: 'var(--navy-900)' }}>
                {mode === 'week'
                  ? `${fmtDate(c.dateDebut)} — ${fmtDate(c.dateFin)}`
                  : fmtDate(c.dateDebut)
                }
              </div>
              <div style={{ fontSize: '.8rem', color: 'var(--muted)', marginTop: 2 }}>
                {c.totalColisLivres} colis · {fmt(c.gainLivreur)} TND gain
              </div>
            </div>
            <StatutBadge statut={c.statut} />
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Page principale ──────────────────────────────────────────────────────────

export default function LivreurCaisse() {
  const [mode, setMode]           = useState<'day' | 'week'>('day')
  const [showBankModal, setShowBankModal] = useState(false)
  const queryClient = useQueryClient()

  const { data: caisse, isLoading } = useQuery({
    queryKey:       ['caisse', mode],
    queryFn:        () => caissesAPI.getMaCaisse(mode),
    refetchInterval: 60_000,
  })

  const { data: historique } = useQuery({
    queryKey: ['caisse-historique', mode],
    queryFn:  () => caissesAPI.getHistorique(mode),
  })

  // Filtre la caisse courante de l'historique (même _id)
  const pastCaisses = (historique?.caisses ?? []).filter(c => c._id !== caisse?._id)

  return (
    <>
      {showBankModal && caisse && (
        <BankInfoModal
          caisseId={caisse._id}
          current={caisse.virementInfo}
          onClose={() => setShowBankModal(false)}
          onSuccess={() => {
            setShowBankModal(false)
            queryClient.invalidateQueries({ queryKey: ['caisse', 'week'] })
          }}
        />
      )}

      <div className="page-head">
        <div className="ph-titles">
          <h1>Ma Caisse</h1>
          <p>Suivi des montants collectés et de vos gains.</p>
        </div>
      </div>

      {/* Toggle Jour / Semaine */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, maxWidth: 480 }}>
        {(['day', 'week'] as const).map(m => (
          <button
            key={m}
            onClick={() => setMode(m)}
            style={{
              flex: 1, padding: '10px 0', borderRadius: 12,
              border: `2px solid ${mode === m ? '#1D9E75' : 'var(--line)'}`,
              background: mode === m ? '#1D9E75' : '#fff',
              color:      mode === m ? '#fff' : 'var(--navy-700)',
              fontWeight: 600, fontSize: '.9rem', cursor: 'pointer',
              transition: 'all .2s',
            }}
          >
            {m === 'day' ? '📅 Journée' : '📆 Semaine'}
          </button>
        ))}
      </div>

      <div style={{ maxWidth: 480 }}>
        {isLoading ? (
          <div style={{
            background: 'linear-gradient(135deg,#1e3a5f,#2d6a9f)',
            borderRadius: 18, padding: '22px 20px', marginBottom: 20,
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[80, 60, 70, 50, 90].map((w, i) => (
                <Skeleton key={i} width={`${w}%`} height={14} />
              ))}
            </div>
          </div>
        ) : caisse ? (
          <CaisseCard
            caisse={caisse}
            onEditBank={() => setShowBankModal(true)}
          />
        ) : null}

        {pastCaisses.length > 0 && (
          <HistoriqueSection caisses={pastCaisses} mode={mode} />
        )}
      </div>

      <style>{`
        @keyframes shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </>
  )
}
